import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useIntlToast from 'hooks/useIntlToast';
import {
  getQueue,
  removeFromQueue,
  markInFlight,
} from '../../../utils/offlineQueue';
import { mutationRegistry } from '../../../config/mutationRegistry';
import logger from '../../../utils/logger';
import { getIsDevelopment } from '../../../constants/environment';
import useOfflineStatus from 'hooks/useOfflineStatus';

const LOG_GROUP = 'OfflineSyncManager';

/**
 * Offline Support Scope
 * ─────────────────────
 * This manager handles replaying mutations that were queued while the user
 * temporarily lost connectivity.
 *
 * Supported:
 *   • Syncing mutations queued when user temporarily goes offline mid-session.
 *   • Rehydrating queue across hard reloads or different browser tabs.
 *
 * Out of scope (not supported):
 *   • Navigating to a page whose JS chunk has not yet been fetched while offline
 *     (lazy-loaded routes will show a ChunkLoadErrorBoundary fallback).
 *   • File/attachment uploads queued while offline.
 */
const getVariableSummary = (variables, mutationKey) => {
  if (!variables) return 'null';
  if (mutationKey === 'SAVE_FORM_STATE') {
    const sections = ['household', 'wagesAndIncome', 'assets', 'deductions'];
    const populated = sections
      .map((s) => {
        const count = Object.keys(
          variables.forms?.[s]?.formValues || {},
        ).length;
        return count > 0 ? `${s}(${count} fields)` : null;
      })
      .filter(Boolean);
    return `Form(Sections: ${populated.join(', ') || 'None'})`;
  }
  if (mutationKey === 'SAVE_SECTIONS_PROGRESS') {
    const progress =
      variables.requestData?.sectionsProgress?.application?.progress;
    return `Progress(${progress}%)`;
  }
  return `Keys(${Object.keys(variables).join(', ')})`;
};

const createLog = (message, data, status) => {
  let dataStr = '';
  if (data) {
    const cleanData = { ...data };
    if (cleanData.variables) {
      cleanData.summary = getVariableSummary(
        cleanData.variables,
        cleanData.mutationKey,
      );
      delete cleanData.variables;
    }
    if (cleanData.items) {
      cleanData.items = cleanData.items.map((i) => ({
        id: i.id,
        key: i.mutationKey,
        summary: getVariableSummary(i.variables, i.mutationKey),
      }));
    }
    dataStr = ` | Data: ${JSON.stringify(cleanData)}`;
  }
  const statusIcon =
    status === 'error' ? '❌' : status === 'success' ? '✅' : 'ℹ️';
  console.log(`${statusIcon} [Sync] ${message}${dataStr}`);
};

const OfflineSyncManager = () => {
  const queryClient = useQueryClient();
  const showIntlToast = useIntlToast();
  const { isOnline } = useOfflineStatus();
  const isSyncingRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (!isOnline) {
      createLog('Skipping sync — browser is offline');
      return;
    }

    if (isSyncingRef.current) {
      createLog('Sync already in progress — skipping');
      return;
    }

    const queue = getQueue();
    if (queue.length === 0) {
      return;
    }

    isSyncingRef.current = true;

    try {
      // Surgically cancel only the queries that are likely to be stale due to
      // offline changes. We leave static metadata (context, property info)
      // alone so the UI doesn't get stuck if those haven't finished yet.
      createLog(
        'Network back online: Cancelling form-related queries to prevent stale data race',
      );
      await queryClient.cancelQueries({
        queryKey: ['getAffordaPortalFormByPortalUserId'],
      });
      await queryClient.cancelQueries({
        queryKey: ['getHouseholdMembers'],
      });

      while (true) {
        const currentQueue = getQueue();

        if (currentQueue.length === 0) {
          createLog('Queue is empty, nothing to sync');
          break;
        }

        createLog('Starting sync batch', {
          queueLength: currentQueue.length,
          items: currentQueue,
        });

        showIntlToast('info', 'app.offlineSync.syncing', {
          count: currentQueue.length,
        });

        let successCount = 0;
        let failureCount = 0;

        for (const item of currentQueue) {
          const { id, mutationKey, variables } = item;
          const mutationFn = mutationRegistry[mutationKey];

          if (mutationFn) {
            try {
              markInFlight(id, true);
              createLog('Replaying mutation', { id, mutationKey, variables });

              const response = await mutationFn(variables);
              removeFromQueue(id);
              successCount++;

              // Auto-heal the queue: If a form was created, it returns a new database ID.
              // Any pending queue items for this user with id:null must be updated
              // with the new ID immediately to prevent duplicate record creation.
              if (mutationKey === 'SAVE_FORM_STATE' && response?.id) {
                const newId = response.id;
                const healQueue = getQueue();
                let healed = false;

                healQueue.forEach((qItem) => {
                  if (
                    qItem.mutationKey === 'SAVE_FORM_STATE' &&
                    qItem.variables.forms.portalUserId ===
                      variables.forms.portalUserId &&
                    !qItem.variables.forms.id
                  ) {
                    qItem.variables.forms.id = newId;
                    healed = true;
                  }
                });

                if (healed) {
                  createLog('Auto-healed pending queue items with new form ID');
                  localStorage.setItem(
                    'FORTRESS_OFFLINE_QUEUE',
                    JSON.stringify(healQueue),
                  );
                }
              }

              createLog('Mutation success', { id, mutationKey }, 'success');
            } catch (error) {
              markInFlight(id, false);
              failureCount++;
              createLog('Mutation failed', { id, mutationKey, error }, 'error');
              console.error(`Failed to sync item ${id}`, error);
              const status =
                error?.statusCode ??
                error?.affordaPortal?.statusCode ??
                error?.response?.status;
              if (status >= 400 && status < 500) {
                createLog(
                  `Removing unrecoverable item (${status})`,
                  { id, mutationKey },
                  'error',
                );
                removeFromQueue(id);
              } else {
                // If it's a 5xx or network error, stop the loop and retry later
                createLog('Recoverable error — stopping sync batch', {
                  id,
                  mutationKey,
                });
                break;
              }
            }
          } else {
            createLog(
              'No registry entry for mutation key — removing',
              { id, mutationKey },
              'error',
            );
            console.warn(`No registry entry for mutation key: ${mutationKey}`);
            removeFromQueue(id);
            failureCount++;
          }
        }

        const remainingQueue = getQueue();

        createLog(
          'Sync batch complete',
          { successCount, failureCount, remaining: remainingQueue.length },
          'success',
        );

        if (remainingQueue.length === 0) {
          showIntlToast('success', 'app.offlineSync.success');
          window.dispatchEvent(new CustomEvent('offlineSyncComplete'));

          createLog('Sync complete: Triggering background cache refresh');

          // Background invalidate - no longer blocking the UI
          queryClient.invalidateQueries({
            queryKey: ['getAffordaPortalFormByPortalUserId'],
          });
          queryClient.invalidateQueries({ queryKey: ['getHouseholdMembers'] });
          queryClient.invalidateQueries({ queryKey: ['affordaDetails'] });
          queryClient.invalidateQueries({ queryKey: ['getProfileInfo'] });
        } else {
          showIntlToast('warning', 'app.offlineSync.partial', {
            count: remainingQueue.length,
          });
          // If we have remaining items due to failures, stop the while loop to avoid infinite attempts
          break;
        }
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [isOnline, queryClient, showIntlToast]);

  useEffect(() => {
    if (isOnline) {
      processQueue();
    }
  }, [isOnline, processQueue]);

  useEffect(() => {
    const handleQueueUpdated = () => {
      if (isOnline) {
        processQueue();
      }
    };

    window.addEventListener('offlineQueueUpdated', handleQueueUpdated);
    return () => {
      window.removeEventListener('offlineQueueUpdated', handleQueueUpdated);
    };
  }, [isOnline, processQueue]);

  return null;
};

export default OfflineSyncManager;
